import React, { useState, useEffect, forwardRef, useImperativeHandle, Fragment } from 'react';
import {
  Box,
  VStack,
  HStack,
  Button,
  Text,
  Grid,
  GridItem,
  useToast,
  Divider,
  Badge,
  IconButton,
  Tooltip
} from '@chakra-ui/react';
import { 
  AddIcon, 
  DeleteIcon, 
  EditIcon, 
  DragHandleIcon,
  ViewIcon,
  SettingsIcon
} from '@chakra-ui/icons';

/**
 * ReactPlannerViewer Component
 * 
 * A mockup implementation of React Planner interface
 * Provides an alternative approach to floor planning with a more
 * component-based, drag-and-drop interface
 */
const ReactPlannerViewer = forwardRef(({ 
  floorPlanData, 
  onUpdate, 
  sidebarWidth = 300, 
  isSidebarCollapsed = false 
}, ref) => {
  const [plannerMode, setPlannerMode] = useState('design'); // 'design', 'catalog', 'properties'
  const [selectedElement, setSelectedElement] = useState(null);
  const [snapPoints, setSnapPoints] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [draggedElement, setDraggedElement] = useState(null);
  const [dragPreviewPosition, setDragPreviewPosition] = useState({ x: 0, y: 0 });
  const [planData, setPlanData] = useState({
    scene: {
      layers: {
        'layer-1': {
          id: 'layer-1',
          name: 'Ground Floor',
          opacity: 1,
          visible: true,
          elements: []
        }
      },
      selectedLayer: 'layer-1'
    }
  });
  const [catalogItems, setCatalogItems] = useState([
    { id: 'room', name: 'Room', category: 'structure', icon: '🏠', width: 200, height: 150, color: 'blue.50' },
    { id: 'wall', name: 'Wall', category: 'structure', icon: '🧱', width: 100, height: 8, color: 'gray.400' },
    { id: 'door', name: 'Door', category: 'openings', icon: '🚪', width: 30, height: 8, color: 'brown.200' },
    { id: 'window', name: 'Window', category: 'openings', icon: '🪟', width: 40, height: 8, color: 'blue.200' },
    { id: 'sofa', name: 'Sofa', category: 'furniture', icon: '🛋️', width: 80, height: 40, color: 'green.100' },
    { id: 'table', name: 'Table', category: 'furniture', icon: '🪑', width: 60, height: 60, color: 'yellow.100' },
    { id: 'bed', name: 'Bed', category: 'furniture', icon: '🛏️', width: 80, height: 60, color: 'purple.100' },
    { id: 'light', name: 'Light', category: 'lighting', icon: '💡', width: 20, height: 20, color: 'yellow.200' }
  ]);
  
  const toast = useToast();

  useImperativeHandle(ref, () => ({
    saveScene: () => {
      handleSave();
    },
    clearScene: () => {
      handleClear();
    },
    exportScene: () => {
      return planData;
    }
  }));

  // Load initial data
  useEffect(() => {
    if (floorPlanData) {
      // Convert existing floor plan data to React Planner format
      console.log('Loading floor plan data into React Planner:', floorPlanData);
    }
  }, [floorPlanData]);

  // Update snap points when elements change
  useEffect(() => {
    const currentLayer = getCurrentLayer();
    if (currentLayer?.elements) {
      const newSnapPoints = calculateSnapPoints(currentLayer.elements);
      setSnapPoints(newSnapPoints);
    }
  }, [planData]);

  const handleSave = () => {
    if (onUpdate) {
      onUpdate(planData);
    }
    toast({
      title: 'Scene saved successfully',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleClear = () => {
    setPlanData({
      scene: {
        layers: {
          'layer-1': {
            id: 'layer-1',
            name: 'Ground Floor',
            opacity: 1,
            visible: true,
            elements: []
          }
        },
        selectedLayer: 'layer-1'
      }
    });
    setSelectedElement(null);
  };

  // Calculate snap points based on existing elements
  const calculateSnapPoints = (elements) => {
    const points = [];
    const SNAP_DISTANCE = 20;
    
    elements.forEach(element => {
      // Add corner and edge snap points
      points.push(
        { x: element.x, y: element.y, type: 'corner' },
        { x: element.x + element.width, y: element.y, type: 'corner' },
        { x: element.x, y: element.y + element.height, type: 'corner' },
        { x: element.x + element.width, y: element.y + element.height, type: 'corner' },
        { x: element.x + element.width / 2, y: element.y, type: 'edge' },
        { x: element.x + element.width / 2, y: element.y + element.height, type: 'edge' },
        { x: element.x, y: element.y + element.height / 2, type: 'edge' },
        { x: element.x + element.width, y: element.y + element.height / 2, type: 'edge' }
      );
    });
    
    return points;
  };

  // Get current layer helper function
  const getCurrentLayer = () => {
    return planData.scene.layers[planData.scene.selectedLayer];
  };

  // Find the best room for placement (for windows, doors, furniture)
  const findBestRoom = (x, y, elementType) => {
    const currentLayer = getCurrentLayer();
    const rooms = currentLayer?.elements.filter(el => el.type === 'room') || [];
    
    for (const room of rooms) {
      if (x >= room.x && x <= room.x + room.width && 
          y >= room.y && y <= room.y + room.height) {
        return room;
      }
    }
    return null;
  };

  // Calculate placement position with room-based logic
  const calculatePlacement = (item) => {
    const currentLayer = getCurrentLayer();
    const rooms = currentLayer?.elements.filter(el => el.type === 'room') || [];
    
    if (item.id === 'room') {
      // Place rooms in open space
      return {
        x: 50 + (rooms.length * 220), 
        y: 50 + (Math.floor(rooms.length / 3) * 170)
      };
    }
    
    if (rooms.length === 0 && item.category !== 'structure') {
      toast({
        title: 'Create a room first',
        description: 'Add a room before placing furniture or fixtures',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return null;
    }
    
    // For openings (windows/doors), place on room edges
    if (item.category === 'openings' && rooms.length > 0) {
      const room = rooms[0]; // Use first room for simplicity
      return {
        x: room.x + room.width / 2 - item.width / 2,
        y: room.y // Place on top edge
      };
    }
    
    // For furniture, place inside the first available room
    if (item.category === 'furniture' && rooms.length > 0) {
      const room = rooms[0];
      return {
        x: room.x + 20 + Math.random() * (room.width - item.width - 40),
        y: room.y + 20 + Math.random() * (room.height - item.height - 40)
      };
    }
    
    // Default placement
    return {
      x: 100 + Math.random() * 200,
      y: 100 + Math.random() * 150
    };
  };

  const handleAddElement = (item) => {
    const placement = calculatePlacement(item);
    if (!placement) return; // Placement was cancelled
    
    const newElement = {
      id: `${item.id}-${Date.now()}`,
      type: item.id,
      name: item.name,
      x: placement.x,
      y: placement.y,
      width: item.width,
      height: item.height,
      rotation: 0,
      properties: {
        category: item.category,
        icon: item.icon,
        color: item.color
      }
    };

    setPlanData(prev => ({
      ...prev,
      scene: {
        ...prev.scene,
        layers: {
          ...prev.scene.layers,
          [prev.scene.selectedLayer]: {
            ...prev.scene.layers[prev.scene.selectedLayer],
            elements: [...prev.scene.layers[prev.scene.selectedLayer].elements, newElement]
          }
        }
      }
    }));

    toast({
      title: 'Element added',
      description: `${item.name} added to the scene`,
      status: 'success',
      duration: 1500,
      isClosable: true,
    });
  };

  const handleDeleteElement = (elementId) => {
    setPlanData(prev => ({
      ...prev,
      scene: {
        ...prev.scene,
        layers: {
          ...prev.scene.layers,
          [prev.scene.selectedLayer]: {
            ...prev.scene.layers[prev.scene.selectedLayer],
            elements: prev.scene.layers[prev.scene.selectedLayer].elements.filter(el => el.id !== elementId)
          }
        }
      }
    }));
    setSelectedElement(null);
  };

  // Snap to grid functionality
  const snapToGrid = (x, y, gridSize = 20) => {
    return {
      x: Math.round(x / gridSize) * gridSize,
      y: Math.round(y / gridSize) * gridSize
    };
  };

  // Snap to nearest snap point
  const snapToPoint = (x, y, threshold = 15) => {
    let bestSnap = { x, y };
    let minDistance = threshold;

    snapPoints.forEach(point => {
      const distance = Math.sqrt(Math.pow(x - point.x, 2) + Math.pow(y - point.y, 2));
      if (distance < minDistance) {
        minDistance = distance;
        bestSnap = { x: point.x, y: point.y };
      }
    });

    return bestSnap;
  };

  // Handle drag start
  const handleDragStart = (e, element) => {
    e.preventDefault();
    e.stopPropagation();
    
    const rect = e.currentTarget.getBoundingClientRect();
    const containerRect = e.currentTarget.parentElement.getBoundingClientRect();
    
    setIsDragging(true);
    setDraggedElement(element);
    setSelectedElement(element);
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    setDragPreviewPosition({
      x: element.x,
      y: element.y
    });
  };

  // Handle drag move
  const handleDragMove = (e) => {
    if (!isDragging || !draggedElement) return;

    const containerRect = e.currentTarget.getBoundingClientRect();
    const newX = e.clientX - containerRect.left - dragOffset.x;
    const newY = e.clientY - containerRect.top - dragOffset.y;

    // Apply constraints based on element type
    let constrainedX = Math.max(0, newX);
    let constrainedY = Math.max(0, newY);

    // For openings (windows/doors), try to snap to room edges
    if (draggedElement.properties.category === 'openings') {
      const currentLayer = getCurrentLayer();
      const rooms = currentLayer?.elements.filter(el => el.type === 'room') || [];
      
      // Find the closest room edge to snap to
      let bestSnapDistance = 30; // Maximum snap distance
      let bestSnapPosition = { x: constrainedX, y: constrainedY };

      rooms.forEach(room => {
        // Check top edge
        if (Math.abs(constrainedY - room.y) < bestSnapDistance) {
          if (constrainedX >= room.x - 20 && constrainedX <= room.x + room.width + 20) {
            bestSnapPosition = { x: constrainedX, y: room.y };
            bestSnapDistance = Math.abs(constrainedY - room.y);
          }
        }
        // Check bottom edge
        if (Math.abs(constrainedY - (room.y + room.height)) < bestSnapDistance) {
          if (constrainedX >= room.x - 20 && constrainedX <= room.x + room.width + 20) {
            bestSnapPosition = { x: constrainedX, y: room.y + room.height };
            bestSnapDistance = Math.abs(constrainedY - (room.y + room.height));
          }
        }
        // Check left edge
        if (Math.abs(constrainedX - room.x) < bestSnapDistance) {
          if (constrainedY >= room.y - 20 && constrainedY <= room.y + room.height + 20) {
            bestSnapPosition = { x: room.x, y: constrainedY };
            bestSnapDistance = Math.abs(constrainedX - room.x);
          }
        }
        // Check right edge
        if (Math.abs(constrainedX - (room.x + room.width)) < bestSnapDistance) {
          if (constrainedY >= room.y - 20 && constrainedY <= room.y + room.height + 20) {
            bestSnapPosition = { x: room.x + room.width, y: constrainedY };
            bestSnapDistance = Math.abs(constrainedX - (room.x + room.width));
          }
        }
      });

      constrainedX = bestSnapPosition.x;
      constrainedY = bestSnapPosition.y;
    } else {
      // For other elements, snap to grid and snap points
      const gridSnapped = snapToGrid(constrainedX, constrainedY);
      const pointSnapped = snapToPoint(gridSnapped.x, gridSnapped.y);
      
      constrainedX = pointSnapped.x;
      constrainedY = pointSnapped.y;
    }

    setDragPreviewPosition({
      x: constrainedX,
      y: constrainedY
    });
  };

  // Handle drag end
  const handleDragEnd = (e) => {
    if (!isDragging || !draggedElement) return;

    // Update the element's position in the plan data
    setPlanData(prev => ({
      ...prev,
      scene: {
        ...prev.scene,
        layers: {
          ...prev.scene.layers,
          [prev.scene.selectedLayer]: {
            ...prev.scene.layers[prev.scene.selectedLayer],
            elements: prev.scene.layers[prev.scene.selectedLayer].elements.map(el =>
              el.id === draggedElement.id
                ? { ...el, x: dragPreviewPosition.x, y: dragPreviewPosition.y }
                : el
            )
          }
        }
      }
    }));

    // Reset drag state
    setIsDragging(false);
    setDraggedElement(null);
    setDragOffset({ x: 0, y: 0 });
    setDragPreviewPosition({ x: 0, y: 0 });
  };

  const currentLayer = getCurrentLayer();
  const elementCount = currentLayer ? currentLayer.elements.length : 0;

  return (
    <Box
      position="relative"
      width="100%"
      height="100%"
      bg="gray.50"
      borderRadius="lg"
      overflow="hidden"
    >
      {/* Header Toolbar */}
      <Box bg="white" borderBottom="1px solid" borderColor="gray.200" p={3}>
        <HStack justify="space-between">
          <HStack spacing={2}>
            <Button
              size="sm"
              variant={plannerMode === 'design' ? 'solid' : 'ghost'}
              colorScheme="blue"
              onClick={() => setPlannerMode('design')}
            >
              Design
            </Button>
            <Button
              size="sm"
              variant={plannerMode === 'catalog' ? 'solid' : 'ghost'}
              colorScheme="green"
              onClick={() => setPlannerMode('catalog')}
            >
              Catalog
            </Button>
            <Button
              size="sm"
              variant={plannerMode === 'properties' ? 'solid' : 'ghost'}
              colorScheme="purple"
              onClick={() => setPlannerMode('properties')}
            >
              Properties
            </Button>
          </HStack>
          
          <HStack spacing={2}>
            <Badge colorScheme="blue">{elementCount} elements</Badge>
            {isDragging && draggedElement && (
              <Badge colorScheme="orange" variant="solid">
                Dragging {draggedElement.name} ({dragPreviewPosition.x.toFixed(0)}, {dragPreviewPosition.y.toFixed(0)})
              </Badge>
            )}
            <Button size="sm" colorScheme="blue" onClick={handleSave}>
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={handleClear}>
              Clear
            </Button>
          </HStack>
        </HStack>
      </Box>

      <HStack spacing={0} height="calc(100% - 60px)">
        {/* Side Panel */}
        <Box
          width="250px"
          bg="white"
          borderRight="1px solid"
          borderColor="gray.200"
          height="100%"
          overflowY="auto"
        >
          {plannerMode === 'catalog' && (
            <VStack spacing={3} p={4} align="stretch">
              <Text fontWeight="bold" color="gray.700">Element Catalog</Text>
              <Divider />
              
              {['structure', 'openings', 'furniture', 'lighting'].map(category => (
                <Box key={category}>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={2} textTransform="capitalize">
                    {category}
                  </Text>
                  <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                    {catalogItems
                      .filter(item => item.category === category)
                      .map(item => (
                        <GridItem key={item.id}>
                          <Button
                            size="sm"
                            variant="outline"
                            height="60px"
                            onClick={() => handleAddElement(item)}
                            _hover={{ bg: 'blue.50' }}
                          >
                            <VStack spacing={1}>
                              <Text fontSize="lg">{item.icon}</Text>
                              <Text fontSize="xs">{item.name}</Text>
                            </VStack>
                          </Button>
                        </GridItem>
                      ))}
                  </Grid>
                </Box>
              ))}
            </VStack>
          )}

          {plannerMode === 'design' && (
            <VStack spacing={3} p={4} align="stretch">
              <Text fontWeight="bold" color="gray.700">Scene Elements</Text>
              <Divider />
              
              {(() => {
                const currentLayer = getCurrentLayer();
                return currentLayer?.elements.length === 0 ? (
                  <Text fontSize="sm" color="gray.500">No elements in scene</Text>
                ) : (
                  currentLayer?.elements.map(element => (
                  <HStack
                    key={element.id}
                    p={2}
                    bg={selectedElement?.id === element.id ? 'blue.50' : 'gray.50'}
                    borderRadius="md"
                    cursor="pointer"
                    onClick={() => setSelectedElement(element)}
                  >
                    <Text fontSize="lg">{element.properties.icon}</Text>
                    <VStack align="start" spacing={0} flex={1}>
                      <Text fontSize="sm" fontWeight="medium">{element.name}</Text>
                      <Text fontSize="xs" color="gray.500">
                        {element.x.toFixed(0)}, {element.y.toFixed(0)}
                      </Text>
                    </VStack>
                    <IconButton
                      size="xs"
                      variant="ghost"
                      icon={<DeleteIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteElement(element.id);
                      }}
                    />
                  </HStack>
                  ))
                );
              })()}
            </VStack>
          )}

          {plannerMode === 'properties' && (
            <VStack spacing={3} p={4} align="stretch">
              <Text fontWeight="bold" color="gray.700">Properties</Text>
              <Divider />
              
              {selectedElement ? (
                <VStack spacing={3} align="stretch">
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={1}>Element</Text>
                    <HStack>
                      <Text fontSize="lg">{selectedElement.properties.icon}</Text>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" color="gray.600">{selectedElement.name}</Text>
                        <Badge size="sm" colorScheme="blue">{selectedElement.properties.category}</Badge>
                      </VStack>
                    </HStack>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={1}>Position</Text>
                    <Text fontSize="sm" color="gray.600">
                      X: {selectedElement.x.toFixed(0)}, Y: {selectedElement.y.toFixed(0)}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={1}>Dimensions</Text>
                    <Text fontSize="sm" color="gray.600">
                      {selectedElement.width} × {selectedElement.height} px
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={1}>Rotation</Text>
                    <Text fontSize="sm" color="gray.600">{selectedElement.rotation}°</Text>
                  </Box>
                  {selectedElement.type !== 'room' && (
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={1}>Contains</Text>
                      <Text fontSize="sm" color="gray.600">
                        {findBestRoom(selectedElement.x + selectedElement.width/2, selectedElement.y + selectedElement.height/2) 
                          ? `Inside ${findBestRoom(selectedElement.x + selectedElement.width/2, selectedElement.y + selectedElement.height/2).name}`
                          : 'Outside all rooms'
                        }
                      </Text>
                    </Box>
                  )}
                </VStack>
              ) : (
                <Text fontSize="sm" color="gray.500">Select an element to view properties</Text>
              )}
            </VStack>
          )}
        </Box>

        {/* Main Canvas Area */}
        <Box
          flex={1}
          height="100%"
          position="relative"
          bg="white"
          backgroundImage="radial-gradient(circle, #e2e8f0 1px, transparent 1px)"
          backgroundSize="20px 20px"
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          style={{ cursor: isDragging ? 'grabbing' : 'default' }}
        >
          {/* Scene Canvas */}
          <Box
            position="absolute"
            inset={0}
            p={4}
          >
            {/* Snap points visualization */}
            {snapPoints.map((point, index) => (
              <Box
                key={`snap-${index}`}
                position="absolute"
                left={`${point.x - 3}px`}
                top={`${point.y - 3}px`}
                width="6px"
                height="6px"
                bg={point.type === 'corner' ? 'red.400' : 'blue.400'}
                borderRadius="full"
                opacity={isDragging ? 0.8 : 0.4}
                pointerEvents="none"
                zIndex={10}
                transition="opacity 0.2s"
              />
            ))}

            {/* Room edge highlights for opening placement */}
            {isDragging && draggedElement?.properties.category === 'openings' && (() => {
              const currentLayer = getCurrentLayer();
              const rooms = currentLayer?.elements.filter(el => el.type === 'room') || [];
              
              return rooms.map(room => (
                <Fragment key={`room-edges-${room.id}`}>
                  {/* Top edge */}
                  <Box
                    position="absolute"
                    left={`${room.x}px`}
                    top={`${room.y - 2}px`}
                    width={`${room.width}px`}
                    height="4px"
                    bg="orange.300"
                    opacity={0.6}
                    pointerEvents="none"
                    zIndex={2}
                  />
                  {/* Bottom edge */}
                  <Box
                    position="absolute"
                    left={`${room.x}px`}
                    top={`${room.y + room.height - 2}px`}
                    width={`${room.width}px`}
                    height="4px"
                    bg="orange.300"
                    opacity={0.6}
                    pointerEvents="none"
                    zIndex={2}
                  />
                  {/* Left edge */}
                  <Box
                    position="absolute"
                    left={`${room.x - 2}px`}
                    top={`${room.y}px`}
                    width="4px"
                    height={`${room.height}px`}
                    bg="orange.300"
                    opacity={0.6}
                    pointerEvents="none"
                    zIndex={2}
                  />
                  {/* Right edge */}
                  <Box
                    position="absolute"
                    left={`${room.x + room.width - 2}px`}
                    top={`${room.y}px`}
                    width="4px"
                    height={`${room.height}px`}
                    bg="orange.300"
                    opacity={0.6}
                    pointerEvents="none"
                    zIndex={2}
                  />
                </Fragment>
              ));
            })()}

            {/* Elements */}
            {(() => {
              const currentLayer = getCurrentLayer();
              return currentLayer?.elements.map(element => {
                // Use drag preview position if this element is being dragged
                const isBeingDragged = isDragging && draggedElement?.id === element.id;
                const displayX = isBeingDragged ? dragPreviewPosition.x : element.x;
                const displayY = isBeingDragged ? dragPreviewPosition.y : element.y;
                
                return (
                  <Box
                    key={element.id}
                    position="absolute"
                    left={`${displayX}px`}
                    top={`${displayY}px`}
                    width={`${element.width}px`}
                    height={`${element.height}px`}
                    bg={selectedElement?.id === element.id ? 'blue.200' : element.properties.color || 'white'}
                    border="2px solid"
                    borderColor={selectedElement?.id === element.id ? 'blue.500' : 'gray.400'}
                    borderRadius={element.type === 'room' ? 'lg' : 'md'}
                    cursor={isBeingDragged ? 'grabbing' : 'grab'}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    transform={`rotate(${element.rotation}deg)`}
                    onClick={() => !isDragging && setSelectedElement(element)}
                    onMouseDown={(e) => handleDragStart(e, element)}
                    _hover={{ borderColor: 'blue.400', opacity: 0.9 }}
                    opacity={element.type === 'room' ? 0.3 : (isBeingDragged ? 0.8 : 1)}
                    zIndex={element.type === 'room' ? 1 : (isBeingDragged ? 100 : 5)}
                    boxShadow={selectedElement?.id === element.id ? 'lg' : (isBeingDragged ? 'xl' : 'sm')}
                    transition={isBeingDragged ? 'none' : 'all 0.2s'}
                    userSelect="none"
                  >
                    <VStack spacing={0}>
                      <Text fontSize={element.width < 30 ? 'sm' : 'lg'}>
                        {element.properties.icon}
                      </Text>
                      {element.width > 40 && (
                        <Text fontSize="xs" color="gray.700" fontWeight="medium">
                          {element.name}
                        </Text>
                      )}
                    </VStack>
                    
                    {/* Room dimensions display */}
                    {element.type === 'room' && (
                      <Text
                        position="absolute"
                        bottom="2px"
                        right="4px"
                        fontSize="xs"
                        color="gray.600"
                        fontWeight="bold"
                      >
                        {element.width}×{element.height}
                      </Text>
                    )}

                    {/* Drag indicator */}
                    {isBeingDragged && (
                      <Box
                        position="absolute"
                        top="-8px"
                        right="-8px"
                        width="16px"
                        height="16px"
                        bg="blue.500"
                        borderRadius="full"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text fontSize="xs" color="white">⟲</Text>
                      </Box>
                    )}
                  </Box>
                );
              });
            })()}

            {/* Help text when empty */}
            {(() => {
              const currentLayer = getCurrentLayer();
              return currentLayer?.elements.length === 0 && (
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                textAlign="center"
                color="gray.400"
              >
                <VStack spacing={2}>
                  <Text fontSize="lg">🏗️</Text>
                  <Text fontSize="md">React Planner Interface</Text>
                  <Text fontSize="sm">Start by adding a Room from the Catalog</Text>
                  <Text fontSize="xs" color="gray.400">Then add windows, doors, and furniture inside</Text>
                </VStack>
                </Box>
              );
            })()}
          </Box>

          {/* Floating Action Button */}
          {plannerMode !== 'catalog' && (
            <Tooltip label="Switch to Catalog">
              <IconButton
                position="absolute"
                bottom={4}
                right={4}
                colorScheme="blue"
                icon={<AddIcon />}
                isRound
                size="lg"
                onClick={() => setPlannerMode('catalog')}
              />
            </Tooltip>
          )}
        </Box>
      </HStack>
    </Box>
  );
});

ReactPlannerViewer.displayName = 'ReactPlannerViewer';

export default ReactPlannerViewer;